import React, { useMemo, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Card, Select } from "antd";
import { ExportToExcel } from "../../../../utils";

const { Option } = Select;

export const ChartComparativeBrand = ({ data }) => {
  const [selectedBrand, setSelectedBrand] = useState("TODOS");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const colors = [
    "#0a9396",
    "#001219",
    "#94d2bd",
    "#e9d8a6",
    "#f8ad9d",
    "#ee9b00",
    "#ca6702",
    "#ae2012",
  ];

  const transformedData = useMemo(() => {
    const dataByDate = {};
    const brandSet = new Set();

    data.forEach((item) => {
      item.products.forEach((subItem) => {
        brandSet.add(subItem.brand);
      });
    });

    const brands = [...brandSet];

    data.forEach((item) => {
      const date = item.dateStart.toDate().toLocaleDateString();

      if (!dataByDate[date]) {
        dataByDate[date] = {};
        brands.forEach((brand) => {
          dataByDate[date][`${brand}`] = 0;
          dataByDate[date][`${brand}_ordenes`] = 0;
          dataByDate[date][`${brand}_productos`] = 0;
          dataByDate[date][`${brand}_total`] = 0;
          dataByDate[date][`${brand}_entregado`] = 0;
          dataByDate[date][`${brand}_ticket_promedio`] = 0;
        });
      }

      item.products?.forEach((subItem) => {
        const brand = subItem.brand;
        const amount = Number(subItem.total.toFixed(2));
        dataByDate[date][`${brand}`] += amount;
        dataByDate[date][`${brand}_ordenes`] += 1;
        dataByDate[date][`${brand}_productos`] += subItem.quantity;
        dataByDate[date][`${brand}_total`] += amount;
        dataByDate[date][`${brand}_ticket_promedio`] =
          dataByDate[date][`${brand}_ordenes`] > 0
            ? dataByDate[date][`${brand}_productos`] /
            dataByDate[date][`${brand}_ordenes`]
            : 0;
        if (item.status === "Entregado") {
          dataByDate[date][`${brand}_entregado`] += amount;
        }
      });
    });

    return Object.keys(dataByDate)?.map((date) => ({
      date,
      ...dataByDate[date],
    }));
  }, [data]);

  const brands = useMemo(() => {
    const brandsSet = new Set();
    data?.forEach((item) => {
      item.products?.forEach((subItem) => {
        brandsSet?.add(subItem.brand);
      });
    });
    return [...brandsSet];
  }, [data]);


  const viewData = useMemo(() => {
    const sellersSet = new Set();
    const brandsSet = new Set();

    data?.forEach((item) => {
      sellersSet?.add(item.seller);
      item.products.forEach((subItem) => {
        brandsSet.add(subItem.brand);
      });
    });

    const sellers = [...sellersSet].sort((a, b) => a.localeCompare(b));
    const brands = [...brandsSet].sort((a, b) => a.localeCompare(b));

    const container = [];

    sellers?.forEach((seller) => {
      brands?.forEach((brand) => {
        const record = {
          "vendedor": seller,
          "marca": brand,
          "venta_total": 0,
          "cant_ordenes": 0,
          "cant_productos": 0,
        };
        container.push(record);
      });
    });

    data?.forEach((item) => {

      container?.forEach((record) => {
        if (record["vendedor"] === item.seller) {
          let brandReference = 0;
          item.products?.forEach((product) => {
            if (record["marca"] === product.brand) {
              brandReference += 1;
              record["venta_total"] += Number(product.total?.toFixed(2));
              record["cant_productos"] += product.quantity;
            }
          });
          if (brandReference > 0) {
            record["cant_ordenes"] += 1;
          }
        }
      });

    });

    return container;
  }, [data]);

  const viewDailyData = useMemo(() => {

    const sellersSet = new Set();
    const brandsSet = new Set();
    const datesSet = new Set();

    data?.forEach((item) => {
      const date = item.dateStart.toDate().toLocaleDateString();
      sellersSet?.add(item.seller);
      item.products.forEach((subItem) => {
        brandsSet.add(subItem.brand);
      });
      datesSet?.add(date);
    });

    const sellers = [...sellersSet].sort((a, b) => a.localeCompare(b));
    const brands = [...brandsSet].sort((a, b) => a.localeCompare(b));
    const dates = [...datesSet];

    const container = [];

    dates?.forEach((date) => {
      sellers?.forEach((seller) => {
        brands?.forEach((brand) => {
          const record = {
            "fecha": date,
            "vendedor": seller,
            "marca": brand,
            "venta_total": 0,
            "cant_ordenes": 0,
            "cant_productos": 0,
          };
          container.push(record);
        });
      });
    });

    data?.forEach((item) => {

      const date = item.dateStart.toDate().toLocaleDateString();

      container?.forEach((record) => {
        if (record["fecha"] === date) {
          if (record["vendedor"] === item.seller) {
            let brandReference = 0;
            item.products?.forEach((product) => {
              if (record["marca"] === product.brand) {
                brandReference += 1;
                record["venta_total"] += Number(product.total?.toFixed(2));
                record["cant_productos"] += product.quantity;
              }
            });
            if (brandReference > 0) {
              record["cant_ordenes"] += 1;
            }
          }
        }
      });
    });

    return container;

  }, [data]);

  const lines = useMemo(() => {
    const brandsToDisplay =
      selectedBrand && selectedBrand !== "TODOS" ? [selectedBrand] : brands;
    return brandsToDisplay?.map((brand, index) => (
      <Line
        type="monotone"
        dataKey={brand}
        stroke={colors[index % colors.length]}
        key={index}
        strokeWidth={2}
        dot={false}
      ></Line>
    ));
  }, [brands, colors, selectedBrand]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const brandData = payload?.find((item) => item.name === selectedBrand);

      return (
        <div className="custom-tooltip">
          <b className="label">{`${label}`}</b>
          {payload?.map((item, index) => (
            <p key={index} className="desc" style={{ color: item.color }}>
              {selectedBrand === item.name
                ? ""
                : `${item.name} : S/ ${item.value.toFixed(2)}`}
            </p>
          ))}
          {brandData && selectedBrand !== "TODOS" && (
            <>
              <p className="desc">
                Total: S/{" "}
                {brandData.payload[`${selectedBrand}_total`].toFixed(2)}
              </p>
              <p className="desc">
                Total Entregado: S/{" "}
                {brandData.payload[`${selectedBrand}_entregado`].toFixed(2)}
              </p>
              <p className="desc">
                Total de Órdenes:{" "}
                {brandData.payload[`${selectedBrand}_ordenes`]}
              </p>
              <p className="desc">
                Total de Productos:{" "}
                {brandData.payload[`${selectedBrand}_productos`]}
              </p>
              <p className="desc">
                Ticket Promedio: S/{" "}
                {brandData.payload[`${selectedBrand}_ticket_promedio`].toFixed(
                  2
                )}
              </p>
            </>
          )}
        </div>
      );
    }
    return null;
  };

  const exportViewData = viewData?.map((record) => {
    const row = { ...record };
    row["venta_total"] = `S/ ${record["venta_total"]?.toFixed(2)}`;
    row["ticketPromedio"] = record["cant_ordenes"] > 0 ? (record["cant_productos"] / record["cant_ordenes"])?.toFixed(2) : 0;
    return row;
  });

  const exportViewDailyData = viewDailyData?.map((record) => {
    const row = { ...record };
    row["venta_total"] = `S/ ${record["venta_total"]?.toFixed(2)}`;
    row["ticketPromedio"] = record["cant_ordenes"] > 0 ? (record["cant_productos"] / record["cant_ordenes"])?.toFixed(2) : 0;
    return row;
  });

  const exportColumns = [
    { title: "Vendedor", dataIndex: "vendedor", key: "vendedor", width: 200 },
    { title: "Marca", dataIndex: "marca", key: "marca", width: 200 },
    { title: "Venta total", dataIndex: "venta_total", key: "venta_total", width: 200 },
    { title: "Cant ordenes", dataIndex: "cant_ordenes", key: "cant_ordenes", width: 100 },
    { title: "Cant productos", dataIndex: "cant_ordenes", key: "cant_ordenes", width: 100 },
    { title: "Ticket promedio", dataIndex: "ticketPromedio", key: "ticketPromedio", width: 100 },
  ];

  const exportDailyColumns = [
    { title: "Fecha", dataIndex: "fecha", key: "fecha", width: 200 },
    { title: "Vendedor", dataIndex: "vendedor", key: "vendedor", width: 200 },
    { title: "Marca", dataIndex: "marca", key: "marca", width: 200 },
    { title: "Venta total", dataIndex: "venta_total", key: "venta_total", width: 200 },
    { title: "Cant ordenes", dataIndex: "cant_ordenes", key: "cant_ordenes", width: 100 },
    { title: "Cant productos", dataIndex: "cant_ordenes", key: "cant_ordenes", width: 100 },
    { title: "Ticket promedio", dataIndex: "ticketPromedio", key: "ticketPromedio", width: 100 },
  ];

  return (
    <Card
      title={
        <div className="flex justify-center items-center gap-1 lg:justify-between">
          Comparativa de Ventas por Marca
          <Select
            className="w-48"
            defaultValue="TODOS"
            onChange={(value) =>
              setSelectedBrand(value === "TODOS" ? null : value)
            }
          >
            <Option value="TODOS">TODOS</Option>
            {brands?.map((brand, index) => (
              <Option key={index} value={brand}>
                {brand}
              </Option>
            ))}
          </Select>
        </div>
      }
      bordered={true}
    >
      <ResponsiveContainer width="100%" height={300}>
        <LineChart
          data={transformedData}
          margin={{
            top: 20,
            right: 20,
            left: 0,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.5} />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          {lines}
        </LineChart>
      </ResponsiveContainer>
      <style jsx>{`
        .custom-tooltip {
          background-color: white;
          border: 1px solid #ccc;
          padding: 10px;
          font-size: 14px;
        }
      `}</style>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportViewData}
          columns={exportColumns}
          fileName="ventasVendedorMarca"
          name="Detalle general"
        />
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: 10,
          marginRight: 20,
        }}
      >
        <ExportToExcel
          data={exportViewDailyData}
          columns={exportDailyColumns}
          fileName="ventasDiariasVendedorCanalCierre"
          name="Detalle diario"
        />
      </div>
    </Card>
  );
};
